import React, { useRef } from "react";
import styles from "./styles.module.scss";
import NextLeft from "@/assets/nextleft.svg";
import NextRight from "@/assets/nextright.svg";
import BlackPlus from "@/assets/BlackPlus.svg";
import useBlogs from "@/hooks/useBlogs";

// Format "2024-04-14" -> "14 APR 2024"
const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
    .format(d)
    .toUpperCase();
};

// Slugify blog titles for URLs
const slugify = (s = "") =>
  s
    .toString()
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

/** Normalize any SourceFlow image field into a usable URL string */
const getImageUrl = (img) => {
  if (!img) return "";
  if (typeof img === "string") return img;

  // Arrays: ["url"], [{ url }], [{ src }], etc.
  if (Array.isArray(img)) {
    const first = img[0];
    if (!first) return "";
    if (typeof first === "string") return first;
    return first.url || first.src || first.path || "";
  }

  // Objects: { url }, { src }, { path }
  return img.url || img.src || img.path || "";
};

export const LatestNews = ({
  overline = "Our Insights",
  heading = "Latest Blogs",
  ctaText = "View all blogs",
  ctaUrl = "/blogs",
  showArrows = true,
  className = "",
}) => {
  const rowRef = useRef(null);
  const blogs = useBlogs({ limit: 6 });

  const scrollByCard = (dir) => {
    if (!rowRef.current) return;
    const cardWidth = 500 + 20; // keep in sync with SCSS
    rowRef.current.scrollBy({ left: dir * cardWidth, behavior: "smooth" });
  };

  return (
    <section className={`${styles.wrapper} ${className}`}>
      {/* Header */}
      <div className={styles.headerRow}>
        <div className={styles.headerText}>
          <div className={styles.overline} dangerouslySetInnerHTML={{ __html: overline }} />
          <h2 className={styles.heading} dangerouslySetInnerHTML={{ __html: heading }} />
        </div>

        {showArrows && (
          <div className={styles.arrows}>
            <button className={`${styles.arrow} ${styles.left}`} aria-label="Previous" onClick={() => scrollByCard(-1)}>
              <NextLeft />
            </button>
            <button className={styles.arrow} aria-label="Next" onClick={() => scrollByCard(1)}>
              <NextRight />
            </button>
          </div>
        )}
      </div>

      {/* Cards Row */}
      <div className={styles.cardsRow} ref={rowRef}>
        <div className={styles.blurBlob} aria-hidden="true" />
        {blogs.map((blog, i) => {
          const imageUrl = getImageUrl(blog?.image);
          return (
            <article key={i} className={styles.card}>
              {/* Image */}
              <div className={styles.cardImageWrap}>
                {imageUrl ? (
                  <img className={styles.cardImage} src={imageUrl} alt="" />
                ) : (
                  <div className={styles.imagePlaceholder} />
                )}
              </div>

              {/* Body */}
              <div className={styles.cardBody}>
                <div className={styles.metaRow}>
                  {Array.isArray(blog?.blog_type) && blog.blog_type[0] && (
                    <span className={styles.badge}>{blog.blog_type[0]}</span>
                  )}
                  {blog?.publish_date && <span className={styles.date}>{formatDate(blog.publish_date)}</span>}
                </div>
                <div className={styles.cardContent}>
                  <h3 className={styles.cardTitle}>{blog?.title || ""}</h3>

                  {/* Read more row with BlackPlus */}
                  <a className={styles.readMoreRow} href={`/blogs/${slugify(blog?.url_slug)}`}>
                    <span className={styles.readMore}>READ MORE</span>
                    <BlackPlus className={styles.readMoreIcon} />
                  </a>
                </div>

                
              </div>
            </article>
          );
        })}
      </div>

      {/* CTA */}
      {ctaText && (
        <a className={styles.cta} href={ctaUrl}>
          {ctaText}
        </a>
      )}
    </section>
  );
};

export default LatestNews;
